import { GALLERY_PAGE_SIZE, getNextOffset } from '@/lib/offset-pagination';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { listGraphicsResponseSchema } from '@borradh-workspace/contracts';
import {
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { GraphicStatus, ListGraphicsResponse } from '../types';

interface ListGraphicsParams {
  status?: GraphicStatus;
  templateId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Query options for listing graphics
 */
export const listGraphicsQueryOptions = (params?: ListGraphicsParams) => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.templateId) queryParams.set('templateId', params.templateId);
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.offset) queryParams.set('offset', params.offset.toString());
  const queryString = queryParams.toString();

  return queryOptions({
    queryKey: [
      'graphics',
      params?.status,
      params?.templateId,
      params?.limit,
      params?.offset,
    ],
    queryFn: async () => {
      return apiClient.get<ListGraphicsResponse>(
        `graphics${queryString ? `?${queryString}` : ''}`,
        { schema: listGraphicsResponseSchema }
      );
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * List Graphics Hook
 * Returns all graphics for the current organization
 */
export const useListGraphics = (params?: ListGraphicsParams) => {
  const query = useQuery(listGraphicsQueryOptions(params));

  return {
    graphics: query.data?.items ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Offset-paged graphics of one status (ready by default) for the gallery's
 * "Load more" button. The gallery pages failed graphics as a list of their
 * own, so it can say which ones failed (ENG-1155). The list endpoint returns
 * no total, so a short page marks the end.
 */
export const useInfiniteListGraphics = (status: GraphicStatus = 'ready') => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.graphics.infinite(status),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const queryParams = new URLSearchParams({
        status,
        limit: String(GALLERY_PAGE_SIZE),
        offset: String(pageParam),
      });
      return apiClient.get<ListGraphicsResponse>(`graphics?${queryParams}`, {
        schema: listGraphicsResponseSchema,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextOffset(lastPage, allPages, GALLERY_PAGE_SIZE),
    staleTime: 30 * 1000,
  });

  return {
    graphics: query.data?.pages.flatMap((page) => page.items) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
